import type { CustomerOrder } from '@noconflict/contracts';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingProfileComplete } from '../../entities/billing-profile';
import { useAccountPublic } from '../../entities/ledger';
import { orderErrorMessage, useSubmitOrder } from '../../entities/order';
import { useOrgContext } from '../../entities/org';
import { useOrgHasDefaultCard } from '../../entities/payment-method';
import { importOrReload } from '../../shared/lib/import-or-reload';
import { Button } from '../../shared/ui/button';
import { ConfirmDialog } from '../../shared/ui/confirm-dialog';
import { BillingStep } from './billing-step';
import { payPath } from './pay-controls';

/**
 * Stripe and its React wrapper are in a chunk of their own: the project page loads them only when someone with a
 * stored card and `orders.pay` actually opens the one-step dialog.
 */
const SubmitAndPayDialog = lazy(() =>
  importOrReload(() => import('../../features/order/submit-and-pay')).then((m) => ({
    default: m.SubmitAndPayDialog,
  })),
);

/**
 * The submit button of a draft order and what follows it, on each payment path, unchanged by which form the draft
 * came from: an organization on account places the order at once (a plain request when its amounts are hidden);
 * a card organization with a stored card and a payer submits and pays in one step; anyone else submits and is
 * then offered the pay page. Every path sits behind a confirmation. While the org's billing details are incomplete,
 * Submit opens the billing step first (completeness is fetched on mount so the click usually reacts at once; a click
 * before the answer waits for it, and if it cannot be read Submit goes on and the server's refusal shows).
 */
export function SubmitOrder({
  order,
  workspaceId,
  amount,
  disabled = false,
  before,
}: {
  order: CustomerOrder;
  workspaceId: string;
  /** The quoted net price, formatted, for the confirmation; null when it is hidden or not known. */
  amount: string | null;
  disabled?: boolean;
  /** Awaited before the confirmation opens (pending saves); false keeps it closed. */
  before?: () => Promise<boolean>;
}) {
  const { t } = useTranslation('order');
  const { org, canIn } = useOrgContext();
  const submit = useSubmitOrder();
  const [confirming, setConfirming] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [billing, setBilling] = useState(false);
  const complete = useBillingProfileComplete(org.id);
  const canPay = canIn(workspaceId, 'orders.pay');
  // On account the order is placed at once and charged to the org's balance; there is no card path at all.
  const account = useAccountPublic(org.id);
  const onAccount = account.data?.payment_mode === 'account';
  const hiddenAmounts = account.data?.amounts_visible === false;
  // The card itself needs `billing.view`, which an orderer has not got; this is yes or no and nothing else.
  const hasCard = useOrgHasDefaultCard(org.id, canPay && account.data?.payment_mode === 'card');
  const oneStep = !onAccount && canPay && hasCard.data === true;

  return (
    <>
      <Button
        variant="accent"
        disabled={disabled || preparing || submit.isPending}
        onClick={async () => {
          setPreparing(true);
          try {
            if (before && !(await before())) return;
            // Clicked before the answer is in: wait for it. Unreadable: go on, `order_submit` checks anyway.
            const known = complete.isPending ? (await complete.refetch()).data : complete.data;
            if (known === false) setBilling(true);
            else setConfirming(true);
          } finally {
            setPreparing(false);
          }
        }}
      >
        {submit.isPending
          ? t('panel.submitting')
          : hiddenAmounts
            ? t('panel.submitRequest')
            : t('panel.submit')}
      </Button>
      <BillingStep
        open={billing}
        onOpenChange={setBilling}
        onSaved={() => {
          setBilling(false);
          setConfirming(true);
        }}
      />
      {oneStep ? (
        <Suspense fallback={null}>
          <SubmitAndPayDialog
            orderId={order.id}
            payPath={payPath(org.slug, order.id)}
            open={confirming}
            onOpenChange={setConfirming}
          />
        </Suspense>
      ) : null}
      <ConfirmDialog
        open={confirming && !oneStep}
        onOpenChange={setConfirming}
        variant="primary"
        title={
          !onAccount
            ? t('panel.confirmTitle')
            : hiddenAmounts
              ? t('panel.requestTitle')
              : t('panel.accountTitle')
        }
        description={
          !onAccount
            ? amount
              ? t('panel.confirmBodyAmount', { amount })
              : t('panel.confirmBody')
            : hiddenAmounts || !amount
              ? t('panel.requestBody')
              : t('panel.accountBody', { amount })
        }
        confirmLabel={
          !onAccount
            ? t('panel.confirmYes')
            : hiddenAmounts
              ? t('panel.submitRequest')
              : t('panel.accountYes')
        }
        cancelLabel={t('panel.confirmNo')}
        errorMessage={orderErrorMessage}
        onConfirm={async () => {
          await submit.mutateAsync(order.id);
        }}
      />
    </>
  );
}
